import moment from 'moment';
import { BehaviorSubject, forkJoin, Observable, of } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs';

import { AllUtility, AllWolf } from '@shared-constants/contract-conditions';
import { BasicInterfaceTypeEnum } from '@shared-enums/cats.enum';
import { SapFilterOperatorEnum } from '@shared-enums/sap.enum';
import { arrayIsTruthy, findUnique } from '@shared-helpers/array-object-helpers';
import { getHumanReadableDateString } from '@shared-helpers/date-helpers';
import { safeCompare } from '@shared-helpers/string-number-helpers';
import { IHttpResponseContract, ISapResponseContractCondition } from '@shared-interfaces/api';

import { IDefaultValueObject } from '../../components/modal/modal-content-default-change/modal-content-default-change.component';
import { IComplexFilterItem, ISelectOptions } from '../../interfaces/internal-interfaces';
import { ISapRequestFilterQueryProps } from '../../services/api.model';
import { DatahubService } from '../../services/datahub.service';
import { ConditionHandler } from '../ConditionHandler';
import { DateHandler } from '../date-handler';
import { Employee } from '../employee';
import { Equipment } from '../equipment';
import { ContractHandler } from './contract-handler';

export class ContractManager {
  //this class stores contract handlers and allows us to shepherd them.

  private readonly readySubject$ = new BehaviorSubject(false); //this one should emit once all the contracts have been found and joined.
  private conditionStore: ConditionHandler[] = [];
  private contractStore: ContractHandler[] = [];

  public readonly ready$ = this.readySubject$.asObservable();

  constructor(private readonly datahub: DatahubService) {
    this.findConditions();
    this.datahub.apiService.catsRecordsSearched$
      .pipe(
        map((records) => {
          const orders = findUnique<string>(records, 'RecOrder');
          orders.forEach((order) => {
            if (!this.contractStore.find((x) => safeCompare(x.getServiceOrder(), order))) {
              this.getNewServiceOrder$(order, this.datahub.weDate.getFilterString()).subscribe({
                next: (_locatedContract) => {
                  //TODO: Use this value somewhere!
                },
                error: (err) => {
                  console.error(
                    `Unable to get the new service order by week ending date "${this.datahub.weDate.getFilterString()}"`,
                    err,
                  );
                },
              });
            }
          });
          return orders;
        }),
        distinctUntilChanged(),
      )
      .subscribe({
        error: (e) => {
          console.error('Error searching for service orders', e);
        },
      });
  }

  public findContractByServiceOrder(serviceOrder: string): ContractHandler | undefined {
    //pull a service order from memory
    //this should always return an observable, even if its in memory. that way if for whatever reason it is not, we can look it up without crashing.
    //gotta admit, the way we look the service order up as we fetch cats records is pretty smart tho.
    if (!serviceOrder) {
      this.datahub.messageService.error(
        'This contract does not have a service order. The Contract Must have a service order to be billed properly. Contact Daveytime Admin.',
      );
      throw Error('You attempted to look for a contract without supplying a service order number.');
    }
    const found = this.contractStore.find((x) => safeCompare(x.value.ServiceOrder, serviceOrder));
    if (found == null) {
      //TODO: if not found in the list of user contracts. Then it should be able to find it by SO. Either we cache them ALL or add a parameter to search by the unique id.
      this.search$({ ServiceOrder: serviceOrder })
        .pipe(map((values) => values[0]))
        .subscribe({
          next: (_locatedContract) => {
            //TODO: Use this value somewhere!
          },
          error: (err) => {
            console.error(`Unable to look up contract by service order "${serviceOrder}"`, err);
          },
        });
    } else {
      if (found.__areConditionsFetched) return found;
      found.getContractConditions(this.datahub);
    }
    return found;
  }

  public getNewServiceOrder$(serviceOrder: string, date: string): Observable<ContractHandler | undefined> {
    //get an observable of service order, then add it to memory. If in memory, as observable.
    const found = this.contractStore.find((x) => safeCompare(x.value.ServiceOrder, serviceOrder));
    if (found) {
      if (found.__areConditionsFetched) return of(found);
      found.getContractConditions(this.datahub);
      return of(found);
    } else {
      return this.search$({ ServiceOrder: serviceOrder, ValidTo: date }).pipe(
        map((values) => {
          if (!arrayIsTruthy(values)) {
            const readableDate = getHumanReadableDateString(moment(date.replace(/datetime'(.+?)'/, '$1')));
            throw new Error(
              `Service order ${serviceOrder} could not be found in the system for week ending ${readableDate}. Please contact Davey Time.`,
            );
          }
          return values[0];
        }),
      );
    }
  }

  public setDefaultBillcode(entity: Employee | Equipment, defaultObj: IDefaultValueObject, serviceOrder: ContractHandler): void {
    if (defaultObj.currentBillcode !== defaultObj.new) {
      const custGrp = serviceOrder.value.CustGroup;
      const condGrp = serviceOrder.value.CondGroup;
      const outgoing = entity.generateBillcodeDefaultInterface(
        serviceOrder.value.CustGroup,
        serviceOrder.value.CondGroup,
        defaultObj.new,
        defaultObj.activityFirstDigit,
      );
      this.datahub.apiService.updateCreateContractDefault$(outgoing).subscribe({
        next: () => {
          const type = entity.billCodeType;
          const contracts = this.findContractsByCondition(custGrp, condGrp);

          if (type === BasicInterfaceTypeEnum.Employee) {
            contracts.forEach((x) => {
              x.updateEmployeeDefault(outgoing);
            });
          } else if (type === BasicInterfaceTypeEnum.Equipment) {
            contracts.forEach((x) => {
              x.updateEquipmentDefault(outgoing);
            });
          }
        },
        error: (e) => {
          console.error('Error updating default billcode', e);
        },
      });
    }
  }

  public getUserContractList(): ISelectOptions[] {
    return this.getSimpleContractsList(this.contractStore);
  }

  public getContractsForUser(): void {
    const costCenters = this.datahub.user.costCentersForApproval;
    if (costCenters.length > 0) {
      this.searchByCostCenter$(costCenters).subscribe({
        next: (contracts) => {
          this.contractStore = contracts;
          this.readySubject$.next(true);
        },
        error: (e) => {
          console.error('Error fetching user contracts', e);
        },
      });
    } else {
      this.readySubject$.next(false);
    }
  }

  public complexSearchByCostCenter$(costCenter: string | string[], dateHandler: DateHandler): Observable<ContractHandler[]> {
    if (!costCenter) throw new Error('You must supply a string or string array to searchByCostCenter function');
    if (!dateHandler) throw new Error('You must supply a dateHandler to use this function.');
    const weekStart: moment.Moment = dateHandler.getWeekStartingDate();
    const filterArray: IComplexFilterItem[] = [
      { name: 'CostCenter', value: costCenter, operator: SapFilterOperatorEnum.Equals },
      { name: 'ValidFrom', value: dateHandler.getFilterString(), operator: SapFilterOperatorEnum.LessThanOrEquals },
      { name: 'ValidTo', value: dateHandler.getFilterString(weekStart), operator: SapFilterOperatorEnum.GreaterThanOrEquals },
    ];
    return this.datahub.apiService.getContracts$(filterArray).pipe(
      map((rawContracts) => {
        return this.storeAndProcessRawContracts(rawContracts);
      }),
    );
  }

  public searchByCostCenter$(costCenter: string | string[], dateHandler = new DateHandler()): Observable<ContractHandler[]> {
    //dateHandler defaults to today if none provided.
    //todo: we don't need both these functions, right?
    const weekStart: moment.Moment = dateHandler.getWeekStartingDate();
    const queryParams = {
      CostCenter: costCenter,
      ValidTo: dateHandler.getFilterString(),
      ValidFrom: dateHandler.getFilterString(weekStart),
    };
    return this.search$(queryParams);
  }

  public getCustomerConditionFromStore(condGrp: string, custGrp: string): ConditionHandler | undefined {
    return this.conditionStore.find((x) => x.CondGroup === condGrp && x.CustGroup === custGrp);
  }

  private search$(params: ISapRequestFilterQueryProps): Observable<ContractHandler[]> {
    return this.datahub.apiService.getContracts$(params).pipe(
      map((rawContracts) => {
        return this.storeAndProcessRawContracts(rawContracts);
      }),
    );
  }

  private storeAndProcessRawContracts(rawContracts: IHttpResponseContract[]): ContractHandler[] {
    return rawContracts.map((rawContract) => {
      const contract = new ContractHandler().setValue(rawContract);
      // if(!contract.value.CondGroup || !contract.value.CustGroup) return;
      const found = this.getCustomerConditionFromStore(rawContract.CondGroup, rawContract.CustGroup);
      if (found) contract.parseAndSetConditions(found);
      else contract.getContractConditions(this.datahub);
      this.contractStore.push(contract);
      return contract;
    });
  }

  private findConditions(): void {
    const utility$ = this.datahub.apiService.getContractConditions$(AllUtility);
    const wolf$ = this.datahub.apiService.getContractConditions$(AllWolf);
    forkJoin([utility$, wolf$])
      .pipe(map(([u, w]) => u.concat(w)))
      .subscribe({
        next: (conditions) => {
          this.handleConditionsFound(conditions);
        },
        error: (e) => {
          console.error('Error fetching contract conditions', e);
        },
      });
  }

  private handleConditionsFound(conditions: ISapResponseContractCondition[]): void {
    this.conditionStore = conditions.map((x) => new ConditionHandler(x));
    this.readySubject$.next(true);
  }

  private findContractsByCondition(custGrp: string, condGrp: string): ContractHandler[] {
    return this.contractStore.filter((x) => x.value.CustGroup === custGrp && x.value.CondGroup === condGrp);
  }

  private getSimpleContractsList(contracts: ContractHandler[]): ISelectOptions[] {
    return contracts.map((x) => {
      return {
        value: x.value.ServiceOrder,
        description: `${x.value.CustomerName}: ${x.value.ItemText}`,
      };
    });
  }
}
